const GA_MEASUREMENT_ID = "G-TPZ85KP17F";
const RUN_NUMBER_STORAGE_KEY = "eggato:analytics:run-number";

type AnalyticsValue = string | number | boolean;

export type GameAnalyticsEvent =
  | "game_complete"
  | "game_over"
  | "game_restart"
  | "game_start"
  | "level_end"
  | "level_start"
  | "play_60_seconds"
  | "run_abandon"
  | "run_progress"
  | "run_resume"
  | "run_setup_opened"
  | "unlock_achievement";

export type AnalyticsParams = Record<
  string,
  AnalyticsValue | null | undefined
>;

interface AnalyticsWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

interface GameAnalyticsOptions {
  measurementId?: string;
  window?: AnalyticsWindow;
  document?: Document;
  debug?: boolean;
}

let fallbackRunNumber = 0;
type RunNumberStorage = Pick<Storage, "getItem" | "setItem">;

export function beginAnalyticsRun(
  resumed: boolean,
  storage: RunNumberStorage | undefined = sessionStorageIfAvailable(),
): number {
  let currentRunNumber = fallbackRunNumber;
  try {
    const storedRunNumber = Number(storage?.getItem(RUN_NUMBER_STORAGE_KEY));
    if (Number.isInteger(storedRunNumber) && storedRunNumber > 0) {
      currentRunNumber = storedRunNumber;
    }
  } catch {
    // In-memory counting still supports browsers that block session storage.
  }

  const runNumber = resumed && currentRunNumber > 0
    ? currentRunNumber
    : currentRunNumber + 1;
  fallbackRunNumber = runNumber;
  try {
    storage?.setItem(RUN_NUMBER_STORAGE_KEY, String(runNumber));
  } catch {
    // Analytics must never prevent the game from starting.
  }
  return runNumber;
}

function sessionStorageIfAvailable(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export class GameAnalytics {
  readonly #measurementId: string;
  readonly #window: AnalyticsWindow;
  readonly #document: Document;
  readonly #debug: boolean;
  #initialized = false;

  constructor(options: GameAnalyticsOptions = {}) {
    this.#measurementId = options.measurementId ?? GA_MEASUREMENT_ID;
    this.#window = options.window ?? window;
    this.#document = options.document ?? document;
    this.#debug =
      options.debug ??
      new URLSearchParams(this.#window.location.search).has("ga_debug");
  }

  initialize(): void {
    if (this.#initialized) return;
    this.#initialized = true;

    const dataLayer = (this.#window.dataLayer ??= []);
    this.#window.gtag ??= function gtag() {
      dataLayer.push(arguments);
    };

    if (!this.#document.querySelector("[data-eggato-ga]")) {
      const script = this.#document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.#measurementId)}`;
      script.dataset.eggatoGa = "true";
      this.#document.head.append(script);
    }

    this.#window.gtag("js", new Date());
    this.#window.gtag("config", this.#measurementId, {
      allow_ad_personalization_signals: false,
      allow_google_signals: false,
      debug_mode: this.#debug,
      send_page_view: true,
    });
  }

  track(event: GameAnalyticsEvent, params: AnalyticsParams = {}): void {
    this.initialize();
    this.#window.gtag?.("event", event, {
      ...params,
      debug_mode: this.#debug,
    });
  }
}

export const analytics = new GameAnalytics();
